(() => {
  const namespaceKey = 'GuruShopFacetDrawerEnhancements';
  const sourceScript = document.currentScript;
  const requestedRootId = sourceScript?.dataset.facetDrawerRoot;
  const existingApi = window[namespaceKey];

  if (existingApi) {
    existingApi.initById(requestedRootId);
    return;
  }

  const initializedRoots = new WeakSet();

  const initRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || initializedRoots.has(sectionRoot)) return false;

    initializedRoots.add(sectionRoot);

    const sectionId = sectionRoot.id.startsWith('shopify-section-')
      ? sectionRoot.id.slice('shopify-section-'.length)
      : sectionRoot.id;

    const normalizeText = (value) =>
      (value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    const facetSearchState = new WeakMap();

    const ensureFacetSearchState = (searchFilter) => {
      if (!(searchFilter instanceof HTMLElement)) return null;

      const existingState = facetSearchState.get(searchFilter);
      if (existingState) return existingState;

      const searchInput = searchFilter.querySelector('[data-facet-search]');
      const optionElements = Array.from(searchFilter.querySelectorAll('[data-facet-option]'));

      if (!(searchInput instanceof HTMLInputElement) || optionElements.length === 0) {
        return null;
      }

      const state = {
        searchInput,
        options: optionElements.map((option) => {
          const sourceLabel =
            option.getAttribute('data-facet-label') ||
            option.querySelector('label')?.textContent ||
            option.textContent ||
            '';

          return {
            element: option,
            normalizedLabel: normalizeText(sourceLabel)
          };
        }),
        headers: Array.from(searchFilter.querySelectorAll('[data-facet-header]')),
        emptyState: searchFilter.querySelector('[data-facet-no-results]'),
        debounceTimer: null
      };

      facetSearchState.set(searchFilter, state);
      return state;
    };

    const applyFacetSearchFilter = (searchFilter) => {
      const state = ensureFacetSearchState(searchFilter);
      if (!state) return;

      const tokens = normalizeText(state.searchInput.value).split(' ').filter(Boolean);
      let visibleOptions = 0;

      state.options.forEach((entry) => {
        const isVisible = tokens.length === 0 || tokens.every((token) => entry.normalizedLabel.includes(token));
        entry.element.hidden = !isVisible;

        if (isVisible) visibleOptions += 1;
      });

      state.headers.forEach((header) => {
        let hasVisibleOption = false;
        let nextNode = header.nextElementSibling;

        while (nextNode && !nextNode.hasAttribute('data-facet-header')) {
          if (nextNode.hasAttribute('data-facet-option') && !nextNode.hidden) {
            hasVisibleOption = true;
            break;
          }

          nextNode = nextNode.nextElementSibling;
        }

        header.hidden = !hasVisibleOption;
      });

      if (state.emptyState) {
        state.emptyState.hidden = visibleOptions !== 0;
      }
    };

    const queueFacetSearchFilter = (searchFilter, delay = 90) => {
      const state = ensureFacetSearchState(searchFilter);
      if (!state) return;

      if (state.debounceTimer) {
        window.clearTimeout(state.debounceTimer);
      }

      state.debounceTimer = window.setTimeout(() => {
        applyFacetSearchFilter(searchFilter);
      }, delay);
    };

    const setupFacetSearchFilters = (root) => {
      root.querySelectorAll('[data-facet-search-filter]').forEach((searchFilter) => {
        applyFacetSearchFilter(searchFilter);
      });
    };

    if (!window.__guruFacetSearchDelegated) {
      window.__guruFacetSearchDelegated = true;

      document.addEventListener('input', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-facet-search')) return;

        const searchFilter = target.closest('[data-facet-search-filter]');
        if (!(searchFilter instanceof HTMLElement)) return;

        queueFacetSearchFilter(searchFilter);
      });

      document.addEventListener('search', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-facet-search')) return;

        const searchFilter = target.closest('[data-facet-search-filter]');
        if (!(searchFilter instanceof HTMLElement)) return;

        applyFacetSearchFilter(searchFilter);
      });

      document.addEventListener('keydown', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || !target.hasAttribute('data-facet-search')) return;
        if (event.key !== 'Escape' || target.value === '') return;

        target.value = '';

        const searchFilter = target.closest('[data-facet-search-filter]');
        if (!(searchFilter instanceof HTMLElement)) return;

        applyFacetSearchFilter(searchFilter);
        event.stopPropagation();
      });
    }

    const getPanelFilterParamNames = (drawer) => {
      const namesInDrawer = Array.from(drawer.querySelectorAll('facets-form [name^="filter."]'))
        .map((field) => field.getAttribute('name') || '')
        .filter(Boolean);

      if (namesInDrawer.length === 0) return [];

      const focusParamName = (drawer.getAttribute('data-focus-filter-param') || '').trim();
      if (!focusParamName) return Array.from(new Set(namesInDrawer));

      const isPriceFocus =
        focusParamName === 'filter.v.price' ||
        focusParamName === 'filter.v.price.gte' ||
        focusParamName === 'filter.v.price.lte' ||
        focusParamName.startsWith('filter.v.price');

      const panelNames = namesInDrawer.filter((name) => {
        if (isPriceFocus) {
          return name === 'filter.v.price.gte' || name === 'filter.v.price.lte' || name.startsWith('filter.v.price');
        }

        return name === focusParamName;
      });

      return Array.from(new Set(panelNames.length > 0 ? panelNames : namesInDrawer));
    };

    const hasSelectedDrawerFilters = (drawer) => {
      const panelNames = new Set(getPanelFilterParamNames(drawer));
      const filterInputs = Array.from(drawer.querySelectorAll('facets-form [name^="filter."]')).filter((field) => {
        const name = field.getAttribute('name') || '';
        return panelNames.has(name);
      });

      return filterInputs.some((field) => {
        if (field.disabled) return false;

        if (field instanceof HTMLInputElement) {
          if (field.type === 'checkbox' || field.type === 'radio') {
            return field.checked;
          }

          return field.value.trim() !== '';
        }

        if (field instanceof HTMLSelectElement) {
          if (field.multiple) {
            return Array.from(field.options).some((option) => option.selected);
          }

          return field.value.trim() !== '';
        }

        if (field instanceof HTMLTextAreaElement) {
          return field.value.trim() !== '';
        }

        return false;
      });
    };

    const buildDrawerPanelClearUrl = (drawer) => {
      const panelNames = getPanelFilterParamNames(drawer);
      const url = new URL(window.location.href);

      panelNames.forEach((name) => {
        url.searchParams.delete(name);
      });

      url.searchParams.delete('page');
      url.searchParams.set('section_id', sectionId);

      return url;
    };

    const setupDrawerClearAction = (drawer) => {
      const clearButton = drawer.querySelector('[data-drawer-clear]');
      const facetsForm = drawer.querySelector('facets-form form');

      if (!(clearButton instanceof HTMLButtonElement) || !(facetsForm instanceof HTMLFormElement)) return;

      const updateVisibility = () => {
        clearButton.hidden = !hasSelectedDrawerFilters(drawer);
      };

      if (clearButton.dataset.enhanced !== 'true') {
        clearButton.dataset.enhanced = 'true';

        clearButton.addEventListener('click', (event) => {
          event.preventDefault();

          const dispatchClearUpdate = () => {
            clearButton.dispatchEvent(
              new CustomEvent('facet:update', {
                bubbles: true,
                detail: {
                  url: buildDrawerPanelClearUrl(drawer)
                }
              })
            );
          };

          if (drawer.hasAttribute('open') && typeof drawer.hide === 'function') {
            drawer.addEventListener('dialog:after-hide', dispatchClearUpdate, { once: true });
            drawer.hide();
          } else {
            dispatchClearUpdate();
          }
        });

        facetsForm.addEventListener('input', updateVisibility);
        facetsForm.addEventListener('change', updateVisibility);
      }

      updateVisibility();
    };

    const setupPriceDrawer = (drawer) => {
      drawer.querySelectorAll('.price-range--drawer').forEach((priceRangeRoot) => {
        if (priceRangeRoot.dataset.enhanced === 'true') return;
        priceRangeRoot.dataset.enhanced = 'true';

        const minInput = priceRangeRoot.querySelector('input[name="filter.v.price.gte"]');
        const maxInput = priceRangeRoot.querySelector('input[name="filter.v.price.lte"]');
        const presetInputs = Array.from(priceRangeRoot.querySelectorAll('[data-price-preset-input]'));
        const facetsForm = drawer.querySelector('facets-form');

        if (!minInput || !maxInput) return;

        const markFormChanged = () => {
          facetsForm?.dispatchEvent(new Event('change', { bubbles: true }));
        };

        const applyPreset = (presetMin, presetMax) => {
          const nextMin = `${presetMin || ''}`.trim();
          const nextMax = `${presetMax || ''}`.trim();

          minInput.value = nextMin;
          maxInput.value = nextMax;

          if (nextMax !== '') {
            maxInput.dispatchEvent(new Event('change', { bubbles: true }));
          } else if (nextMin !== '') {
            minInput.dispatchEvent(new Event('change', { bubbles: true }));
          } else {
            markFormChanged();
          }
        };

        const updateState = () => {
          const minValue = minInput.value.trim();
          const maxValue = maxInput.value.trim();

          presetInputs.forEach((input) => {
            const presetMin = input.getAttribute('data-price-preset-min') || '';
            const presetMax = input.getAttribute('data-price-preset-max') || '';
            input.checked = minValue === presetMin && maxValue === presetMax;
          });
        };

        presetInputs.forEach((input) => {
          input.addEventListener('change', () => {
            const presetMin = input.getAttribute('data-price-preset-min') || '';
            const presetMax = input.getAttribute('data-price-preset-max') || '';

            if (input.checked) {
              applyPreset(presetMin, presetMax);
            } else if (minInput.value.trim() === presetMin && maxInput.value.trim() === presetMax) {
              applyPreset('', '');
            } else {
              updateState();
            }
          });
        });

        minInput.addEventListener('input', updateState);
        maxInput.addEventListener('input', updateState);
        minInput.addEventListener('change', updateState);
        maxInput.addEventListener('change', updateState);
        updateState();
      });
    };

    const setupDrawerEnhancements = (drawer) => {
      setupFacetSearchFilters(drawer);
      setupPriceDrawer(drawer);
      setupDrawerClearAction(drawer);
    };

    const bindDrawer = (drawer) => {
      if (!(drawer instanceof HTMLElement)) return;
      if (drawer.dataset.enhancementsBound === 'true') return;
      drawer.dataset.enhancementsBound = 'true';

      setupDrawerEnhancements(drawer);

      drawer.addEventListener('dialog:after-show', () => {
        const drawerBody = drawer.shadowRoot?.querySelector('[part="body"]');
        if (drawerBody) drawerBody.scrollTop = 0;

        setupDrawerEnhancements(drawer);

        const searchInput = drawer.querySelector('[data-facet-search]');
        if (searchInput instanceof HTMLInputElement) {
          searchInput.focus({ preventScroll: true });
          searchInput.select();
        }
      });
    };

    const bindAllDrawers = () => {
      sectionRoot.querySelectorAll('facets-drawer').forEach((drawer) => {
        bindDrawer(drawer);
      });
    };

    bindAllDrawers();

    const drawerMutationObserver = new MutationObserver((mutations) => {
      const hasRelevantChange = mutations.some((mutation) => mutation.type === 'childList' && (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0));
      if (!hasRelevantChange) return;

      bindAllDrawers();
    });

    drawerMutationObserver.observe(sectionRoot, { childList: true, subtree: true });

    return true;
  };

  const initById = (rootId) => {
    if (!rootId) return false;
    return initRoot(document.getElementById(rootId));
  };

  const initWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    const markerSelector = 'script[data-facet-drawer-root]';

    if (scope.matches?.(markerSelector)) {
      initById(scope.dataset.facetDrawerRoot);
    }

    scope.querySelectorAll(markerSelector).forEach((marker) => {
      initById(marker.dataset.facetDrawerRoot);
    });
  };

  window[namespaceKey] = { initById, initWithin };

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target);
  });

  initById(requestedRootId);
})();
